import type { NavigationProps } from "@wayflyer/flyui";
import {
  IconArrowUpRight20Line,
  IconArrowsRightLeft20Line,
  IconLotOfCash20Line,
  IconMoneyOperations20Line,
  IconPlus20Line,
} from "@wayflyer/flyui-icons/20/line";
import type { BaseSchemas } from "@wayflyer/api-types";
import type { IntlShape } from "react-intl";

import { getNavItemNameFromId } from "./nav-item-strings";

const GLOBAL_ACTIONS_SECTION_ID = "global-actions";

// The server emits this only for an approved banking customer whose user has MFA and the
// account-view permission, which is also what the money-movement flows need. "banking page title"
// is the application entry and does not count.
const BANKING_ACCOUNTS_NAV_ITEM_ID = "banking accounts page title";
const APPLY_FOR_FUNDING_NAV_ITEM_ID = "apply for funding title";

type GlobalActionsSection = NonNullable<NavigationProps["globalSection"]>;

// Section-relative paths of the flows the menu opens. Launching from inside one of them must not
// capture the flow's own URL as the return path, or Close would lead straight back into it.
const FLOW_PATHS = ["banking/payments/send-money", "banking/transfers/create", "banking/deposit/create"];

// Outcome flags a page shows once on landing. Carrying them back would replay the toast or screen.
const ONE_SHOT_FLAGS = [
  "success",
  "successCreate",
  "successClose",
  "cancelled",
  "cancelledCreate",
  "error",
  "errorCreate",
];

// Mirrors the banking section's `isSafeRedirectPath`: relative section paths such as "accounts/123"
// are legitimate return paths, schemes and protocol-relative values are not.
const isInAppPath = (value: string) => {
  const trimmed = value.trimStart();
  return !!trimmed && !/^[/\\]{2}/.test(trimmed) && !/^[a-z][a-z0-9+.-]*:/i.test(trimmed);
};

/**
 * The page a flow should return to when closed, as pathname plus search, or undefined when the menu is
 * opened from inside a flow that has no return path of its own to pass on.
 */
export const getLaunchReturnPath = ({
  customerDomain,
  pathname,
  search,
  state,
}: {
  customerDomain: string;
  pathname: string;
  search: string;
  state: unknown;
}): string | undefined => {
  const params = new URLSearchParams(search);
  const inFlow = FLOW_PATHS.some((flow) => {
    const flowPath = `/${customerDomain}/${flow}`;
    return pathname === flowPath || pathname.startsWith(`${flowPath}/`);
  });

  if (inFlow) {
    const backLink = (state as { backLink?: unknown } | null)?.backLink;
    if (typeof backLink === "string" && backLink) {
      return backLink;
    }
    const redirectTo = params.get("redirectTo");
    return redirectTo && isInAppPath(redirectTo) ? redirectTo : undefined;
  }

  for (const flag of ONE_SHOT_FLAGS) {
    params.delete(flag);
  }
  const query = params.toString();
  return query ? `${pathname}?${query}` : pathname;
};

const withReturnPath = (href: string, returnPath: string | undefined) =>
  returnPath ? `${href}?${new URLSearchParams({ redirectTo: returnPath }).toString()}` : href;

export const getGlobalActionsSection = ({
  intl,
  customerDomain,
  navItems,
  returnPath,
}: {
  intl: IntlShape;
  customerDomain: string;
  navItems: BaseSchemas.NavItem[] | null | undefined;
  /** From {@link getLaunchReturnPath}; carried as `redirectTo` so it survives the step-up reload. */
  returnPath: string | undefined;
}): NavigationProps["globalSection"] => {
  const section = (items: GlobalActionsSection["items"]): GlobalActionsSection => ({
    id: GLOBAL_ACTIONS_SECTION_ID,
    label: intl.formatMessage({ id: "Menu: Quick actions", defaultMessage: "Quick actions" }),
    icon: <IconMoneyOperations20Line />,
    items,
  });

  if ((navItems ?? []).some(({ id }) => id === BANKING_ACCOUNTS_NAV_ITEM_ID)) {
    return section([
      {
        id: "global-action-send",
        label: intl.formatMessage({ id: "Menu: Send", defaultMessage: "Send" }),
        href: withReturnPath(`/${customerDomain}/banking/payments/send-money`, returnPath),
        icon: <IconArrowUpRight20Line />,
      },
      {
        id: "global-action-transfer",
        label: intl.formatMessage({ id: "Menu: Transfer", defaultMessage: "Transfer" }),
        href: withReturnPath(`/${customerDomain}/banking/transfers/create`, returnPath),
        icon: <IconArrowsRightLeft20Line />,
      },
      {
        id: "global-action-deposit",
        label: intl.formatMessage({ id: "Menu: Deposit", defaultMessage: "Deposit" }),
        href: withReturnPath(`/${customerDomain}/banking/deposit/create`, returnPath),
        icon: <IconPlus20Line />,
      },
    ]);
  }

  const applyForFunding = (navItems ?? []).find(({ id }) => id === APPLY_FOR_FUNDING_NAV_ITEM_ID);
  if (applyForFunding) {
    return section([
      {
        id: "global-action-apply-for-funding",
        label: getNavItemNameFromId(intl, applyForFunding.id),
        href: applyForFunding.path,
        icon: <IconLotOfCash20Line />,
      },
    ]);
  }

  return undefined;
};
